import styled from "@emotion/styled";
import { Button, ConfirmModal, PageTable, Title } from "@src/components";
import { queryKeys } from "@src/hooks/queries/queryKeys";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getColumns } from "./BenefitsColumns";
import BenefitsProduct from "./BenefitsProduct";
import { useLocation, useNavigate } from "react-router-dom";
import { css } from "@emotion/react";
import { useQueryClient } from "react-query";
import PartnerBenefitsFilter from "./PartnerBenefitsFilter";
import EditModal from "@src/components/modal/EditModal";
import PartnerBenefitsUser from "./PartnerBenefitsUser";
import { ProvisionUser } from "@src/interfaces/Commodity.interface";
import {
    fetchPartnerSearch,
    setPartnerBenefitUser,
    useRegisteredPartnerList,
    PaidBenefitsType,
    useApplyBenefits,
    usePaidBenefitsList,
} from "@src/hooks/queries/useExpertDetail";

function mapPartnerSearchResponseToProvisionUsers(res: unknown): ProvisionUser[] {
    const source = (res ?? {}) as any;
    const candidates: unknown[] = [res, source?.result, source?.data?.result];
    const raw = (candidates.find((candidate) => Array.isArray(candidate)) as unknown[]) ?? [];
    return raw.map((item: any, idx: number) => ({
        no: item.paybackPartnerNo ?? item.no ?? idx + 1,
        toGo: "",
        userNo: item.userNo ?? 0,
        uname: item.userName ?? item.uname ?? "",
        status: 0,
        comments: item.userId ?? item.comments ?? "",
        partnerCode: item.partnerCode ?? "",
        delete: 0,
        issueNo: 0,
    }));
}

function selectionNoKey(rows: { no?: number }[]): string {
    return rows
        .map((r) => Number(r?.no))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
        .join(",");
}

function serializeFilter(filter: Record<string, any>): string {
    let params = new URLSearchParams();
    for (let key in filter) {
        if (Array.isArray(filter[key])) {
            if (filter[key].length > 0) {
                params.append(key, filter[key].toString());
            }
        } else if (filter[key]) {
            params.append(key, filter[key].toString());
        }
    }
    return params.toString();
}

function parseBenefitsFilterFromSearch(search: string): Record<string, any> {
    const trimmed = search?.startsWith("?") ? search.slice(1) : search ?? "";
    if (!trimmed) return {};
    const q = new URLSearchParams(trimmed);
    const out: Record<string, any> = {};
    const searchColumn = q.get("searchColumn");
    if (searchColumn) out.searchColumn = searchColumn;
    const searchValue = q.get("searchValue");
    if (searchValue != null && searchValue !== "") out.searchValue = searchValue;
    return out;
}

const PartnerNewBenefits = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const pagesInitialState = {
        pageIndex: 0,
        pageSize: 10,
    };
    const initParams = {
        searchColumn: "",
    };

    const queryClient = useQueryClient();

    const [filter, setFilter] = useState<Record<string, any>>(() => ({
        ...initParams,
        ...parseBenefitsFilterFromSearch(location.search),
    }));
    const [selectedRows, setSelectedRows] = useState([]);
    const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
    const [isModalShow, setIsModalShow] = useState<boolean>(false);
    const [isBenefitsProductModal, setIsBenefitsProductModal] = useState(false);
    const [benefitsSelectedProduct, setBenefitsSelectedProduct] = useState<any>(null);
    const [benefitsPartnerRow, setBenefitsPartnerRow] = useState<any>(null);
    const [paidBenefitsByPartner, setPaidBenefitsByPartner] = useState<Record<number, string>>({});
    const [isDisable, setIsDisable] = useState<boolean>(true);
    const [provisionUserList, setProvisionUserList] = useState<ProvisionUser[]>([]);
    const [selectedProvisionUser, setSelectedProvisionUser] = useState<ProvisionUser | null>(null);
    const [userParams, setUserParams] = useState<string[]>([]);
    const { data: paidBenefitsData } = usePaidBenefitsList();
    const applyBenefitsMutation = useApplyBenefits();
    const { data: registeredPartnerData, refetch: refetchRegisteredPartner } = useRegisteredPartnerList(filter);

    const onSelectedRowsChange = useCallback((selectedFlatRows: any) => {
        const selectedRowsData = selectedFlatRows.map((row: any) => row.original);
        setSelectedRows((prev: any[]) => {
            if (selectionNoKey(prev) === selectionNoKey(selectedRowsData)) {
                return prev;
            }
            return selectedRowsData;
        });
    }, []);

    const handleExcludeSelectedUsers = useCallback(async () => {
        if (selectedRows.length === 0) return;

        try {
            for (const row of selectedRows as any[]) {
                const paybackPartnerNo = Number(row?.no ?? 0);
                if (!paybackPartnerNo) continue;
                await setPartnerBenefitUser(paybackPartnerNo);
            }

            await queryClient.invalidateQueries([queryKeys.REGISTERED_PARTNER_LIST]);
            refetchRegisteredPartner();
            setSelectedRows([]);
            setIsOpenConfirmModal(false);
        } catch (err) {
            console.error("파트너 제외 실패", err);
        }
    }, [queryClient, refetchRegisteredPartner, selectedRows]);

    const handleDeleteClick = () => {
        setIsOpenConfirmModal(true);
    };

    useEffect(() => {
        const newQueryParams = serializeFilter(filter);
        navigate(location.pathname + (newQueryParams ? `?${newQueryParams}` : ""), { replace: true });
    }, [filter, navigate, location.pathname]);

    const openBenefitsProductModal = useCallback((partnerRow: any) => {
        setBenefitsPartnerRow(partnerRow);
        setBenefitsSelectedProduct(null);
        setIsBenefitsProductModal(true);
    }, []);

    const handleSelectPaidBenefits = useCallback(
        (partnerRow: any, benefitNo: number | null) => {
            const paybackPartnerNo = Number(partnerRow?.no ?? 0);
            if (!paybackPartnerNo) return;

            setPaidBenefitsByPartner((prev) => ({
                ...prev,
                [paybackPartnerNo]: benefitNo ? String(benefitNo) : "",
            }));

            applyBenefitsMutation.mutate(
                {
                    paybackPartnerNo,
                    benefitCategory: "payment",
                    benefitNo,
                },
                {
                    onSuccess: () => {
                        refetchRegisteredPartner();
                    },
                },
            );
        },
        [applyBenefitsMutation, refetchRegisteredPartner],
    );

    const handleDeleteRegisterBenefits = useCallback(
        (partnerRow: any) => {
            const paybackPartnerNo = Number(partnerRow?.no ?? 0);
            if (!paybackPartnerNo) return;

            applyBenefitsMutation.mutate(
                {
                    paybackPartnerNo,
                    benefitCategory: "register",
                    benefitNo: null,
                },
                {
                    onSuccess: () => {
                        refetchRegisteredPartner();
                    },
                },
            );
        },
        [applyBenefitsMutation, refetchRegisteredPartner],
    );

    const paidBenefitsOptions = useMemo(() => {
        const list: PaidBenefitsType[] = Array.isArray(paidBenefitsData)
            ? paidBenefitsData
            : Array.isArray(paidBenefitsData?.result)
            ? paidBenefitsData.result
            : Array.isArray(paidBenefitsData?.data?.result)
            ? paidBenefitsData.data.result
            : [];

        return list.map((item: PaidBenefitsType) => ({
            key: String(item.no),
            value: item.title ?? "상품",
        }));
    }, [paidBenefitsData]);

    const columns = useMemo(
        () =>
            getColumns(
                navigate,
                openBenefitsProductModal,
                paidBenefitsOptions,
                handleSelectPaidBenefits,
                (partnerRow: any) => paidBenefitsByPartner[Number(partnerRow?.no ?? 0)] ?? "",
                handleDeleteRegisterBenefits,
            ),
        [
            navigate,
            openBenefitsProductModal,
            paidBenefitsOptions,
            handleSelectPaidBenefits,
            paidBenefitsByPartner,
            handleDeleteRegisterBenefits,
        ],
    );
    const registeredPartnerRows = useMemo(() => {
        const source = (registeredPartnerData?.partners ?? []) as any[];

        return source.map((item: any) => {
            const partner = item?.BenefitPartner ?? item ?? {};
            return {
                ...partner,
                no: partner.paybackPartnerNo ?? partner.userNo ?? 0,
                user: {
                    no: partner.userNo ?? 0,
                    info: {
                        uname: partner.userName ?? "",
                    },
                },
            };
        });
    }, [registeredPartnerData]);

    const runPartnerUserSearch = useCallback(
        async (searchValue: string) => {
            try {
                const res = await queryClient.fetchQuery([queryKeys.PARTNER_SEARCH, searchValue], () =>
                    fetchPartnerSearch({ searchValue }),
                );
                const rows = mapPartnerSearchResponseToProvisionUsers(res);
                setProvisionUserList(rows);
            } catch (err) {
                console.error(err);
            }
        },
        [queryClient],
    );

    const handlePartnerUserSearch = useCallback(async () => {
        const searchValue = userParams
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",");
        await runPartnerUserSearch(searchValue);
    }, [userParams, runPartnerUserSearch]);

    const handleResetUserSearchInput = useCallback(async () => {
        setUserParams([]);
        await runPartnerUserSearch("");
    }, [runPartnerUserSearch]);

    useEffect(() => {
        if (!isModalShow) return;
        setSelectedProvisionUser(null);
        runPartnerUserSearch("");
    }, [isModalShow, runPartnerUserSearch]);

    useEffect(() => {
        setIsDisable(selectedProvisionUser === null);
    }, [selectedProvisionUser]);

    return (
        <>
            <StyledWrap>
                <Title title="파트너 혜택 관리" />
                <PartnerBenefitsFilter setFilter={setFilter as any} />
                <div className="btnDiv">
                    <div className="delete_button">
                        <Button
                            color="gd"
                            handleClick={handleDeleteClick}
                            label="선택 회원 삭제"
                            size="medium"
                            isDisabled={selectedRows.length === 0}
                        />
                    </div>
                    <div className="delete_button">
                        <Button
                            color="gd"
                            handleClick={() => {
                                setIsModalShow(true);
                            }}
                            label="유저 추가"
                            size="medium"
                        />
                    </div>
                </div>

                <div css={tableBoxStyle}>
                    <PageTable
                        FixedHeight={700}
                        columns={columns}
                        data={registeredPartnerRows}
                        initialState={pagesInitialState}
                        totalCount={registeredPartnerRows.length}
                        showCheckbox={true}
                        onSelectedRowsChange={onSelectedRowsChange}
                    />
                </div>
            </StyledWrap>
            <ConfirmModal
                isModalShow={isOpenConfirmModal}
                handleCancel={() => {
                    setIsOpenConfirmModal(false);
                }}
                handleOk={() => {
                    handleExcludeSelectedUsers();
                }}
            >
                <>
                    <p
                        style={{
                            fontSize: "22px",
                            fontWeight: "400",
                        }}
                    >
                        파트너 회원에서 삭제하시겠습니까?
                    </p>
                </>
            </ConfirmModal>
            <EditModal
                buttonText="추가"
                handleBtnClick={async () => {
                    const no = Number(selectedProvisionUser?.no);
                    if (!Number.isFinite(no) || no <= 0) return;

                    try {
                        await setPartnerBenefitUser(no);
                    } catch (err) {
                        console.error("파트너 등록 실패", no, err);
                        return;
                    }

                    await queryClient.invalidateQueries([queryKeys.REGISTERED_PARTNER_LIST]);
                    refetchRegisteredPartner();
                    setSelectedProvisionUser(null);
                    setProvisionUserList([]);
                    setUserParams([]);
                }}
                handleOpenModal={() => {
                    setIsModalShow(false);
                    refetchRegisteredPartner();
                    setSelectedProvisionUser(null);
                    setUserParams([]);
                }}
                size="big"
                title="유저 추가"
                isModalShow={isModalShow}
                isDisabled={isDisable}
            >
                <StyledModalWrapper>
                    <PartnerBenefitsUser
                        userParams={userParams}
                        setUserParams={setUserParams}
                        onSearch={handlePartnerUserSearch}
                        onResetSearch={handleResetUserSearchInput}
                        users={provisionUserList}
                        selectedUser={selectedProvisionUser}
                        setSelectedUser={setSelectedProvisionUser}
                    />
                </StyledModalWrapper>
            </EditModal>

            <EditModal
                buttonText="확인"
                handleBtnClick={() => {
                    const paybackPartnerNo = Number(benefitsPartnerRow?.no ?? 0);
                    const benefitNo = Number(benefitsSelectedProduct?.no ?? 0);
                    if (!paybackPartnerNo || !benefitNo) return;

                    applyBenefitsMutation.mutate(
                        {
                            paybackPartnerNo,
                            benefitCategory: "register",
                            benefitNo,
                        },
                        {
                            onSuccess: () => {
                                refetchRegisteredPartner();
                                setIsBenefitsProductModal(false);
                                setBenefitsSelectedProduct(null);
                                setBenefitsPartnerRow(null);
                            },
                        },
                    );
                }}
                handleOpenModal={() => {
                    setIsBenefitsProductModal(false);
                    setBenefitsSelectedProduct(null);
                    setBenefitsPartnerRow(null);
                }}
                size="big"
                title="가입 시 무료 이용 이용권"
                isModalShow={isBenefitsProductModal}
                isDisabled={!benefitsSelectedProduct?.no}
            >
                <BenefitsProduct selectedItem={benefitsSelectedProduct} setSelectedItem={setBenefitsSelectedProduct} />
            </EditModal>
        </>
    );
};

export default PartnerNewBenefits;

const StyledWrap = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 40px;
    .btnDiv {
        display: flex;
        width: 100%;
        justify-content: flex-start;
        flex-direction: row;
        gap: 10px;
    }
    .delete_button {
        display: flex;
        flex-shrink: 0;
    }
`;

const tableBoxStyle = css`
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    height: fit-content;
    td {
        padding: 8px 0;
        &:nth-of-type(1) {
            width: 50px;
            & > div {
                margin: unset;
                width: 100%;
            }
        }
        &:nth-of-type(2) {
            width: 100px;
            & > div {
                margin: unset;
                width: 100%;
            }
        }
        &:nth-of-type(3) {
            width: 80px;
            & > div {
                margin: unset;
                width: 100%;
            }
        }
        &:nth-of-type(4) {
            width: 80px;
            & > div {
                margin: unset;
                width: 100%;
            }
        }
        &:nth-of-type(5) {
            width: 180px;
            & > div {
                margin: unset;
                width: 100%;
            }
        }
        &:nth-of-type(6) {
            width: 250px;
            & > div {
                margin: unset;
                width: 100%;
            }
        }
        &:nth-of-type(7) {
            width: 250px;
            & > div {
                margin: unset;
                width: 100%;
            }
        }
        &:last-of-type {
            width: 80px;
            & > div {
                display: flex;
                justify-content: center;
                width: 100%;
            }
        }
    }
`;

const StyledModalWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;
